import { ReactNode } from 'react';
import { PillToggleSize, PillToggleVariant as PillSwatchVariant } from '@components/pill-toggle';

export const PILL_TOGGLE_GROUP_VARIANT_SEGMENTED = 'segmented';
export const PILL_TOGGLE_GROUP_VARIANT_TABS = 'tabs';
export const PILL_TOGGLE_GROUP_VARIANT_PLAIN_TABS = 'plain-tabs';

export type PillToggleGroupVariant =
  | typeof PILL_TOGGLE_GROUP_VARIANT_SEGMENTED
  | typeof PILL_TOGGLE_GROUP_VARIANT_TABS
  | typeof PILL_TOGGLE_GROUP_VARIANT_PLAIN_TABS;

export interface PillToggleGroupOption<T extends string> {
  value: T;
  label: string;
  /** Per-pill color theme (critical / warning / success / neutral). Drives the SELECTED pill background + text colors. */
  variant: PillSwatchVariant;
  /** Optional badge / dot rendered after the label inside the pill. */
  indicator?: ReactNode;
}

export interface PillToggleGroupProps<T extends string> {
  value: T;
  onChange: (next: T) => void;
  options: PillToggleGroupOption<T>[];
  disabled?: boolean;
  /** Pill height. */
  size?: PillToggleSize;
  /**
   * Use-case variant — drives container density AND unselected-pill styling as one block.
   * Pick the one that matches the consumer's UI pattern; never mix the two looks.
   * - `'segmented'` (default): segmented control. Tight `p-0.5 gap-px` container; unselected pills are
   *   `text-secondary text-body-m`. Used inside FormFieldRow and FormFieldTable's mobile modal.
   * - `'tabs'`: tab strip on a grey track. Comfortable `p-1 gap-1 xs:gap-2` container; unselected
   *   pills are `fill-secondary text-tertiary text-subtitle-m`. Used by `<TranslationFields>` locale tabs.
   * - `'plain-tabs'`: tab strip with NO track (pass `backgroundClassName='bg-transparent'`). Selected
   *   pill is a flat `bg-neutral-3 dark:bg-neutral-70` fill; unselected pills are fully transparent
   *   `text-tertiary` with no background and no hover. Used by the create-admission type modal.
   * `'segmented'` / `'tabs'` pick the SELECTED pill color via `option.variant`; `'plain-tabs'` owns its
   * own selected fill and ignores `option.variant`.
   */
  variant?: PillToggleGroupVariant;
  /** When true, pills stretch to share the container width. */
  fullWidth?: boolean;
  /**
   * Background utility for the container track. Defaults to `'bg-surface-secondary'` (the grey track).
   * Pass e.g. `'bg-transparent'` to render the pills with no visible track.
   */
  backgroundClassName?: string;
  /**
   * Typography utility for the pill labels (selected + unselected). When omitted, keeps the
   * variant default (`tabs` → `text-subtitle-m`, `segmented` → `text-body-m`). Pass e.g. `'text-subtitle-s'`.
   */
  textClassName?: string;
  ariaLabel?: string;
  className?: string;
  /**
   * When set, each option button gets `data-testid={`${dataTestId}--${option.value}`}` so E2E can
   * pick a specific pill (e.g. the "required" segment) without an order-fragile `.nth()` or an
   * i18n label. Off by default — no test-id is emitted unless a consumer opts in.
   */
  dataTestId?: string;
}
